import { Component, ViewChild } from '@angular/core';
import { EmployeeJobPositionService } from './employee-job-position.service';
import { EmployeeJobPositionModel } from './model/employee-job-position.model';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { RequestData, SearchSort } from 'src/app/common/models/request-data.model';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { Router } from '@angular/router';
import { StateManagerService } from 'src/app/common/services/state.manager.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, SortDirection } from '@angular/material/sort';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-employee-job-position',
  templateUrl: './employee-job-position.component.html',
  styleUrls: ['./employee-job-position.component.scss'],
  standalone: false
})
export class EmployeeJobPositionComponent {
  list: EmployeeJobPositionModel[] = [];
  showSearch: boolean = false;
  isLoading = true;
  IsDownloading = false;

  filter: FilterListItemModel[] = [
    { title: "Code", column: "code" },
    { title: "Name", column: "name" },
    { title: "Description", column: "description" },
  ];
  displayedColumns = ['code', 'name', 'description', 'hierarchy_order', 'is_hq', 'grade', 'action'];
  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };
  isHandset: boolean = false;
  searchColumFilter: SearchParam = {};
  columnSortData: SearchSort[] = [];
  selectedItem?: EmployeeJobPositionModel;
  dataSource = new MatTableDataSource(this.list);
  permission: any;

  constructor(private breakpointObserver: BreakpointObserver,
    private service: EmployeeJobPositionService,
    private stateManagerService: StateManagerService,
    public dialog: MatDialog,
    private permissionService: PermissionService,
    private snackBarService: SnackBarService,
    private router: Router) {
    this.permission = permissionService.getPagePermission("employee");
    this.permission.can_add = false;
    this.permission.can_delete = false;
    this.permission.can_edit = false;
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state: BreakpointState) => {
      this.isHandset = state.matches;
    });
  }

  setDataSource(list: EmployeeJobPositionModel[]) {
    this.dataSource = new MatTableDataSource(list);
    this.dataSource.sort = this.sort;
  }

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;

  ngAfterViewInit(): void {
    this.loadPreviousState();
  }

  loadPreviousState(): void {
    var previousState = this.stateManagerService.getState(this.constructor.name);
    if (previousState !== undefined && this.searchFilter != undefined) {
      if (previousState.state.search.filters.scope) {
        this.pagingData.pageIndex = previousState.state.search.filters.scope.offset / previousState.state.search.filters.scope.limit;
        this.pagingData.pageSize = previousState.state.search.filters.scope.limit;
      }
      if (previousState.state.search.sort != undefined && previousState.state.search.sort.length > 0) {
        this.setSortColumn(previousState.state.search.sort[0].column, previousState.state.search.sort[0].order);
      }
      this.searchColumFilter = JSON.parse(JSON.stringify(previousState.state.search.filters));
      this.searchFilter.setSearchFilter(this.searchColumFilter, false);
    } else {
      this.setSortColumn('name', 'asc');
    }
    this.loadData();
  }

  setSortColumn(column: string, direction: SortDirection): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: column, order: direction });
    this.sort.active = column;
    this.sort.direction = direction;
  }

  buildRequestData(): RequestData {
    let searchParam = this.searchColumFilter;
    let requestData: RequestData = {
      search: {
        filters: {
          simpleSearch: searchParam.freeSearch,
          advSearch: searchParam.advSearch || [],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.columnSortData,
      }
    };
    return requestData;
  }

  onFilterApplied(columFilter: any): void {
    this.searchColumFilter = columFilter;
    this.loadData();
  }

  onDelete(info: EmployeeJobPositionModel): void {
    const message = `Are you sure you want to delete ` + (info.name + "?");
    const dialogData = new ConfirmDialogModel("Confirm Deletion", message);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { maxWidth: "400px", data: dialogData });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.service.delete(info).subscribe({
          next: (response) => {
            if (response.status == 'SUCCESS') {
              this.snackBarService.openSnackBar({ type: 'success', title: 'Deleted', message: 'Record deleted successfully.' });
              this.loadData();
            } else {
              this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
            }
          },
          error: () => {
            this.snackBarService.openSnackBar({ message: 'Failed to delete. Connection to the server failed', title: 'Failed!!!', type: 'error' });
          }
        });
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    let requestData = this.buildRequestData();
    this.service.getList(requestData).subscribe(response => {
      if (response.status == 'SUCCESS') {
        const list = response.data.job_positions || [];
        list.forEach((item: any) => {
          item.is_hq = item.is_hq ? Number(item.is_hq) : 0;
          item.is_active = item.is_active ? Number(item.is_active) : 0;
        });
        this.setDataSource(list);
        this.pagingData.length = response.data.total_count;
        if (this.pagingData.length == 0) {
          this.snackBarService.openSnackBar({ message: 'No data found.', title: 'No Data!!!', type: 'warn' });
          this.selectedItem = undefined;
        } else {
          this.onRowSelected(list[0]);
          this.stateManagerService.setState(this.constructor.name, requestData);
        }
      } else {
        this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
      }
      this.isLoading = false;
    });
  }

  onRowSelected(info: EmployeeJobPositionModel): void {
    this.selectedItem = info;
  }

  sortData($event: any): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: this.sort.active, order: this.sort.direction });
    this.loadData();
  }

  onEdit(info: EmployeeJobPositionModel): void {
    this.router.navigate(['employee/employee-job-position-edit', info.id]);
  }

  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadData();
  }

  addClicked(): void {
    this.router.navigate(['employee/employee-job-position-edit', 0]);
  }

  exportClicked() {
    if (this.pagingData.length === 0) {
      this.snackBarService.openSnackBar({ message: 'No data found to export.', title: 'No Data!!!', type: 'warn' });
      return;
    }
    this.isLoading = true;
    this.IsDownloading = true;
    this.service.downloadExcel(this.buildRequestData()).subscribe({
      next: blob => {
        this.IsDownloading = false;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'employee_job_positions.csv';
        link.click();
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      },
      error: () => {
        this.IsDownloading = false;
        this.snackBarService.openSnackBar({ message: 'Failed to export.', title: 'Failed!!!', type: 'error' });
        this.isLoading = false;
      }
    });
  }
}
